import React, {
  useMemo,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { format, parseISO } from 'date-fns';
import ButtonCopy from 'shared-pages/components/buttons/button-copy';

import './grid.css';
import { Bullet } from 'shared-pages/components';
import { formatWeight } from 'shared-pages/utils/helpers';
import { IdocWasteOriginTypes } from 'shared-pages/shared/constants';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    sumsData,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    canAddNew,
    canEdit,
    canDelete,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('ITran'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  // Function to copy a row to a new ITran document
  const handleCopyRow = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('ITran'), { id: -1 }),
        state: {
          ...location.state,
          copyFrom: row.original, // pass the row data for copying
          pathname: location.pathname,
        },
      });
    },
    [history, location, getRouteByName],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: TableCellV8,
        size: 5,
        meta: { disabled: true },
      },
      {
        id: 'code',
        header: t('Κωδικός Διακίνησης'),
        columnName: t('Κωδικός Διακίνησης'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 20,
        meta: { disabled: true },
        footer: () => {
          return <div className="text-start font-weight-normal">Σύνολα</div>;
        },
      },
      {
        id: 'transactionDate',
        header: t('Ημερομηνία Διακίνησης'),
        columnName: t('Ημερομηνία Διακίνησης'),
        accessorKey: 'transactionDate',
        orderByField: 'transactionDate',
        cell: CellDateV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'fromWastereceptionpointId',
        header: t('Προέλευση'),
        columnName: t('Προέλευση'),
        accessorKey: 'fromWastereceptionpoint.name',
        orderByField: 'fromWastereceptionpoint.name',
        cell: ({ getValue, row }) => {
          const rec = row.original;
          if (rec.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage)
            return (
              <div className="text-start">
                <div>{row.original.fromWastereceptionpoint?.name}</div>
                {/* <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {row.original.collectionpoint.addressLine1}
              ,&nbsp;{row.original.collectionpoint.addressArea}
              ,&nbsp;{row.original.collectionpoint.addressCity}
              ,&nbsp;{row.original.collectionpoint.addressDistrict}
            </div> */}
              </div>
            );
          else {
            return (
              <div className="text-start">
                <div>{rec.fromCollectionpoint?.name}</div>
                <div style={{ color: 'gray', fontSize: 'smaller' }}>
                  {rec.fromCollectionpoint?.addressLine1 ?? '-'}
                  ,&nbsp;{rec.fromCollectionpoint?.addressArea}
                  ,&nbsp;{rec.fromCollectionpoint?.addressCity}
                </div>
              </div>
            );
          }
        },
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'toWastereceptionpointId',
        header: t('Προορισμός'),
        columnName: t('Προορισμός'),
        accessorKey: 'toWastereceptionpoint.name',
        orderByField: 'toWastereceptionpoint.name',
        cell: ({ getValue, row }) => (
          <div className="text-start">
            <div>{row.original.toWastereceptionpoint?.name}</div>
            {/* <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {row.original.collectionpoint.addressLine1}
              ,&nbsp;{row.original.collectionpoint.addressArea}
              ,&nbsp;{row.original.collectionpoint.addressCity}
              ,&nbsp;{row.original.collectionpoint.addressDistrict}
            </div> */}
          </div>
        ),
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'internalTransactionLines',
        header: t('Απόβλητα'),
        columnName: t('Απόβλητα'),
        accessorKey: 'internalTransactionLines',
        orderByField: 'internalTransactionLines',
        cell: ({ getValue, row }) => {
          function compare(a, b) {
            if (a.aa < b.aa) {
              return -1;
            }
            if (a.aa > b.aa) {
              return 1;
            }
            return 0;
          }
          const lines = row.original.internalTransactionLines;
          return (
            <div className="text-start">
              {lines.sort(compare).map((v, i, ar) => {
                let bullet = <>{v.aa}:&nbsp;</>;
                if (ar.length <= 1) {
                  bullet = null;
                }
                return (
                  <div key={i + v.aa}>
                    {bullet}
                    {v.material?.name ?? '-'} <Bullet /> {v.containersQty ?? '-'} {t('τμχ')}{' '}
                    <Bullet /> {formatWeight(v.netWeight) ?? '-'} kg
                  </div>
                );
              })}
            </div>
          );
        },
        size: 35,
        footer: () => {
          return (
            <div className="text-start font-weight-normal">
              {sumsData.map((v, i) => {
                return (
                  <div key={i} className="mb-1">
                    {v?.materialName ?? '-'} <Bullet /> {v.totalContainersQty ?? '-'} {t('τμχ')}{' '}
                    <Bullet /> {formatWeight(v.totalNetWeight) ?? '-'} kg
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        id: 'notes',
        header: t('Παρατηρήσεις'),
        columnName: t('Παρατηρήσεις'),
        accessorKey: 'notes',
        orderByField: 'notes',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        orderByField: 'lastUser',
        size: 120,
        cell: ({ row, table }) => {
          const isSelected = row.original.id === selected?.id;
          return true ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '0 2px' }}>
              {' '}
              {canEdit && EditCellV8({ row, table })}{' '}
              {canAddNew && (
                <ButtonCopy
                  className="ms-1"
                  selected={isSelected}
                  onClick={() => handleCopyRow(row)}
                  title={t('Αντιγραφή')}
                />
              )}{' '}
            </div>
          ) : (
            <>-</>
          );
        },
      },
    ],
    [t, handleCopyRow, selected, sumsData],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="itransactions-grid"
          appStore={appStore}
          tableStore={tableStore}
          key={`grid-${selected?.id ?? 'x'}`}
          columns={columns as any}
          showFooter={true}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Κάδο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Κάδος');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
